// src/pages/Jobs/JobCardGridSkeleton.tsx
import React from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';
import LoadingOverlay from '../../Components/LoadingOverlay';

const JobCardSkeleton: React.FC = () => (
  <Card className="h-100 shadow-sm border-0 job-card" style={{ 
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.03)'
  }}>
    <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', height: '100%' }}>
      
      {/* Job Title Section */}
      <div className="mb-3">
        {/* Title - 2 lines */}
        <div 
          className="skeleton-animate mb-2"
          style={{
            width: '85%',
            height: '20px',
            backgroundColor: '#e9ecef',
            borderRadius: '4px'
          }}
        />
        <div 
          className="skeleton-animate mb-2"
          style={{
            width: '60%',
            height: '20px',
            backgroundColor: '#e9ecef',
            borderRadius: '4px'
          }}
        />
        
        {/* Created Date */}
        <div className="d-flex align-items-center">
          <div 
            className="skeleton-animate me-1"
            style={{
              width: '14px',
              height: '14px',
              backgroundColor: '#e9ecef',
              borderRadius: '2px'
            }}
          />
          <div 
            className="skeleton-animate"
            style={{
              width: '80px',
              height: '14px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px'
            }}
          />
        </div>
      </div>

      {/* Image Section - Fixed height 200px */}
      <div className="mb-3">
        <div 
          className="skeleton-animate"
          style={{
            width: '100%',
            height: '200px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            position: 'relative'
          }}
        >
          {/* Image placeholder icon */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#dee2e6',
            fontSize: '24px'
          }}>
            📷
          </div>
        </div>
      </div>

      {/* Address Section - Two lines */}
      <div className="mb-3" style={{ minHeight: '3em' }}>
        <div className="d-flex align-items-start">
          <div 
            className="skeleton-animate me-2 mt-1 flex-shrink-0"
            style={{
              width: '16px',
              height: '16px',
              backgroundColor: '#e9ecef',
              borderRadius: '2px'
            }}
          />
          <div style={{ flex: 1 }}>
            {/* Address line 1 */}
            <div 
              className="skeleton-animate mb-1"
              style={{
                width: '90%',
                height: '16px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px'
              }}
            />
            {/* Address line 2 */}
            <div 
              className="skeleton-animate"
              style={{
                width: '70%',
                height: '16px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Job Summary Section - Exactly 6 lines */}
      <div className="mb-1 flex-grow-1">
        {[...Array(6)].map((_, index) => (
          <div 
            key={index}
            className="skeleton-animate mb-1"
            style={{
              width: index === 5 ? '60%' : '100%', // Last line shorter
              height: '14px',
              backgroundColor: '#e9ecef',
              borderRadius: '4px'
            }}
          />
        ))}
      </div>

      {/* Categories Section */}
      <div className="mb-1">
        <div className="d-flex flex-wrap gap-1">
          {[...Array(3)].map((_, index) => (
            <div 
              key={index}
              className="skeleton-animate"
              style={{
                width: `${Math.random() * 40 + 50}px`,
                height: '24px',
                backgroundColor: '#e9ecef',
                borderRadius: '12px'
              }}
            />
          ))}
        </div>
      </div>

      {/* Divider Line */}
      <hr style={{ margin: '0.5rem 0', opacity: 0.2, borderColor: '#dee2e6' }} />

      {/* Footer Section - Status and Button */}
      <div className="d-flex justify-content-between align-items-center mt-auto">
        {/* Job Status - Left aligned */}
        <div 
          className="skeleton-animate"
          style={{
            width: '80px',
            height: '14px',
            backgroundColor: '#e9ecef',
            borderRadius: '4px'
          }}
        />
        
        {/* View Details Button - Right aligned */}
        <div 
          className="skeleton-animate"
          style={{
            width: '120px',
            height: '32px',
            backgroundColor: '#e9ecef',
            borderRadius: '6px'
          }}
        />
      </div>
    </div>
  </Card>
);

interface JobCardGridSkeletonProps {
  count?: number;
}

const JobCardGridSkeleton: React.FC<JobCardGridSkeletonProps> = ({ count = 9 }) => {
  return (
    <>
      {/* Loading Overlay */}
      <div className="text-center py-5">
        <LoadingOverlay
          isVisible={true}
          size="medium"
          noBlur={true}
          text="BidBro"
          subtext="Loading jobs..."
          backgroundOpacity={0.4}
          debug={false}
        />
      </div>

      {/* Enhanced skeleton animation styles */}
      <style>
        {`
          @keyframes skeleton-pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.6;
            }
          }
          
          .skeleton-animate {
            animation: skeleton-pulse 1.5s ease-in-out infinite;
          }

          /* Match the JobCard hover effects on skeleton */
          .job-card:hover {
            transform: translateY(-8px) scale(1.02) !important;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1) !important;
            z-index: 10;
            position: relative;
          }

          .job-card {
            transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
            will-change: transform, box-shadow;
          }
        `}
      </style>
      
      {/* Skeleton Cards with responsive grid matching JobCardGrid */}
      <div 
        className="job-card-grid-container"
        style={{
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e0 #f7fafc',
          padding: '12px 0'
        }}
      >
        <Row className="g-3" style={{ margin: '0 -0.5rem' }}>
          {[...Array(count)].map((_, index) => (
            <Col 
              key={index} 
              style={{
                minWidth: "380px", 
                width: "400px", 
                maxWidth: "420px", 
                flexShrink: 0
              }}
              className={`
                ${index >= 3 ? 'd-none d-md-block' : ''} 
                ${index >= 6 ? 'd-none d-lg-block' : ''}
              `}
            >
              <JobCardSkeleton />
            </Col>
          ))}
        </Row>

        {/* Add some padding at the bottom for better scrolling UX */}
        <div style={{ height: '20px' }}></div>
      </div>
    </>
  );
};

export default JobCardGridSkeleton;